import { TextField, List, ListItem, ListItemIcon, ListItemText, Paper, CircularProgress, Alert, InputAdornment, useTheme } from "@mui/material";
import { LocationOn, Search } from "@mui/icons-material";
import { WorldRenderer } from "../WorldRenderer";
import { useState, useCallback, useRef, useEffect } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

interface PlaceResult {
  id: string;
  displayName: string;
  formattedAddress: string;
  location: {
    lat: number;
    lng: number;
  };
}

export function PlacesSearch({ wr }: { wr: WorldRenderer }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const theme = useTheme();
  const placesLib = useMapsLibrary("places");
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  const searchPlaces = useCallback(async (searchQuery: string) => {
    if (!placesLib || !searchQuery.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      // Create search request using the new searchByText method
      const request = {
        textQuery: searchQuery,
        fields: ['id', 'displayName', 'formattedAddress', 'location'],
        // Optional: add location bias for better local results
        // locationBias: { center: { lat: 37.7749, lng: -122.4194 }, radius: 50000 },
      };

      const { places } = await placesLib.Place.searchByText(request);

      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      setIsLoading(false);

      if (places && places.length > 0) {
        // Transform to our interface format
        const transformedResults: PlaceResult[] = places.map(place => ({
          id: place.id,
          displayName: place.displayName || 'Unknown Place',
          formattedAddress: place.formattedAddress || 'No address available',
          location: {
            lat: place.location?.lat() || 0,
            lng: place.location?.lng() || 0,
          }
        }));

        setResults(transformedResults);
        setShowResults(true);
        setError(null);
      } else {
        setResults([]);
        setShowResults(true);
        setError(null);
      }
    } catch (err) {
      // Don't show error if request was aborted (user typed again)
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      setIsLoading(false);
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
      setShowResults(false);
      console.error('Places search error:', err);
    }
  }, [placesLib]);

  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search for very short queries
    if (newQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    // Debounce the search
    searchTimeoutRef.current = setTimeout(() => {
      searchPlaces(newQuery);
    }, 300);
  }, [searchPlaces]);

  const handlePlaceSelect = useCallback((place: PlaceResult) => {
    // Use the transformed location data directly
    wr.planetService.pointCameraTo(
      {
        lat: place.location.lat,
        lon: place.location.lng,
        alt: 50,
      },
      1000
    );

    // Clear search results and close dropdown
    setQuery(place.displayName);
    setShowResults(false);
    setResults([]);
  }, [wr]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      handlePlaceSelect(results[0]);
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  }, [results, handlePlaceSelect]);

  // Click outside to close results
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} style={{
      position: 'relative', width: '400px',
    }}>
      <TextField
        size="small"
        variant="outlined"
        value={query}
        sx={{
          width: "100%",
          '& .MuiOutlinedInput-root': {
            height: '22px',
            marginRight: '10px',
            marginTop: '1px',
          }
        }}
        onChange={(e) => handleQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search Map"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }
        }}
      />

      {showResults && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '290px',
            overflow: 'auto',
            mt: 1,
          }}
        >
          {error ? (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          ) : results.length === 0 && !isLoading ? (
            <ListItem>
              <ListItemText primary="No results found" />
            </ListItem>
          ) : (
            <List dense>
              {results.map((place) => (
                <ListItem
                  key={place.id}
                  onClick={() => handlePlaceSelect(place)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary={place.displayName}
                    secondary={place.formattedAddress}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </div>
  );
}